import React, { createContext, useContext, useEffect, useState } from "react";
import { fetchContent, fetchCategories } from "../services/api";
import { heroSlides, whyChooseUs, factory, categories as mockCategories, company, featuredProjects, testimonials, galleryItems } from "../mock/mockData";

const ContentContext = createContext(null);

const fallback = {
  hero: heroSlides,
  stats: factory.stats,
  whyChooseUs,
  footerAbout:
    "Factory-direct manufacturer of custom medals, sportswear and event merchandise for sporting events, clubs and promotional buyers worldwide.",
  officesLine: "Hong Kong \u2022 United Kingdom \u2022 Spain",
  categoriesSection: { eyebrow: "Product Range", title: "Explore Our Product Categories" },
  factorySection: {
    eyebrow: "Factory & Manufacturing",
    title: "Manufacturing Strength Meets International Service",
    text: "With over 5,000m2 of production space, 150+ skilled workers and advanced machinery, we control the entire process — from mould development and casting to plating, sublimation and final inspection.",
    gallery: factory.gallery.slice(0, 4),
  },
  projectsSection: { eyebrow: "Featured Projects", title: "Successful Projects Worldwide", items: featuredProjects },
  testimonialsSection: { eyebrow: "Testimonials", title: "What Our Clients Say", items: testimonials },
  factoryPage: {
    heroEyebrow: "Factory & Manufacturing",
    heroTitle: "Inside Our Manufacturing Facility",
    heroSubtitle: "Real production, real capacity, real quality control — the manufacturing power of China combined with international standards.",
    heroImage: factory.gallery[1],
    aboutEyebrow: "About Our Manufacturing",
    aboutTitle: "Full In-House Production Control",
    aboutText1: "Our facility spans over 5,000m2 with 150+ skilled workers and advanced machinery.",
    aboutText2: "With more than 12 years of export experience, we deliver factory-direct pricing without compromising on quality.",
    aboutImages: factory.gallery.slice(0, 4),
    process: factory.process,
    qualityControl: [
      { title: "Material Inspection", desc: "Every raw material is checked before it enters production." },
      { title: "In-Process Inspection", desc: "Continuous checks during casting, plating and finishing." },
      { title: "Final Inspection", desc: "100% final QC before careful packing and shipping." },
    ],
    certifications: factory.certifications,
  },
  galleryPage: {
    eyebrow: "Gallery",
    title: "Our Project Portfolio",
    subtitle: "A showcase of medals, running events, teamwear and promotional products produced for clients worldwide.",
    items: galleryItems,
  },
};

export const ContentProvider = ({ children }) => {
  const [content, setContent] = useState(fallback);
  const [categories, setCategories] = useState(mockCategories);

  const loadContent = async () => {
    try {
      const data = await fetchContent();
      setContent({
        hero: data.hero?.length ? data.hero : fallback.hero,
        stats: data.stats?.length ? data.stats : fallback.stats,
        whyChooseUs: data.whyChooseUs?.length ? data.whyChooseUs : fallback.whyChooseUs,
        footerAbout: data.footerAbout || fallback.footerAbout,
        officesLine: data.officesLine || fallback.officesLine,
        categoriesSection: data.categoriesSection || fallback.categoriesSection,
        factorySection: data.factorySection || fallback.factorySection,
        projectsSection: data.projectsSection || fallback.projectsSection,
        testimonialsSection: data.testimonialsSection || fallback.testimonialsSection,
        factoryPage: data.factoryPage || fallback.factoryPage,
        galleryPage: data.galleryPage || fallback.galleryPage,
      });
    } catch (e) {
      // keep fallback
    }
  };

  const loadCategories = async () => {
    try {
      const data = await fetchCategories();
      if (data?.length) setCategories(data);
    } catch (e) {
      // keep fallback
    }
  };

  useEffect(() => {
    loadContent();
    loadCategories();
  }, []);

  return (
    <ContentContext.Provider value={{ content, categories, refresh: loadContent, refreshCategories: loadCategories }}>
      {children}
    </ContentContext.Provider>
  );
};

export const useContent = () =>
  useContext(ContentContext) || { content: fallback, categories: mockCategories, refresh: () => {}, refreshCategories: () => {} };
export { company };
